import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  ListItemIcon
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { FileDownloadOutlinedIcon, InsertDriveFileOutlinedIcon } from 'icons/materialIcons';
import UpgradeSnackBar from 'components/common/notification/UpgradeSnackBar';
import NotificationSnackbar from 'components/common/notification/NotificationSnackbar';
import ExportUtils from 'utils/contacts/ExportUtils';

const ExportModal = ({
  isOpen,
  onClose,
  anchorEl,
  onExport,
  onExportCSV,
  onExportExcel,
  contacts = [],
  selectedCount = 0,
  isSingleContact = false,
  positionAdjustment = { horizontal: 0 }
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [isUpgradeSnackbarOpen, setIsUpgradeSnackbarOpen] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('Upgrade to Pro or Pro Plus for access to integrations');
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Reset upgrade notification when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsUpgradeSnackbarOpen(false);
    }
  }, [isOpen]);

  const handleUpgradeSnackbarClose = () => {
    setIsUpgradeSnackbarOpen(false);
  };

  const handleSubscribe = () => {
    handleUpgradeSnackbarClose();
    navigate('/dashboard/subscribe');
  };

  const handleNotificationClose = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  const showNotification = (message, severity = 'success') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  // Handle CSV export
  const handleExportToCSV = () => {
    if (onExportCSV) {
      onExportCSV();
    } else if (contacts) {
      try {
        if (isSingleContact && contacts._id) {
          // Single contact export
          ExportUtils.exportSingleLead(contacts);
          showNotification(`Successfully exported ${contacts.leadName || contacts.leadEmail || 'contact'} to CSV`);
        } else if (Array.isArray(contacts) && contacts.length > 0) {
          // Multiple contacts export
          const timestamp = new Date().toISOString().slice(0, 10);
          const filename = selectedCount > 0
            ? `Selected_Contacts_${selectedCount}_${timestamp}.csv`
            : `Contacts_Export_${timestamp}.csv`;
          ExportUtils.exportMultipleLeads(contacts, filename);
          showNotification(
            selectedCount > 0
              ? `Successfully exported ${selectedCount} contacts to CSV`
              : `Successfully exported ${contacts.length} contacts to CSV`
          );
        }
      } catch (error) {
        showNotification('Failed to export to CSV. Please try again.', 'error');
      }
      onClose();
    } else {
      showNotification('No contacts available to export', 'warning');
    }
  };

  // Handle Excel export
  const handleExportToExcel = () => {
    if (onExportExcel) {
      onExportExcel();
    } else if (contacts) {
      try {
        if (isSingleContact && contacts._id) {
          // Single contact export
          ExportUtils.exportSingleLeadToExcel(contacts);
          showNotification(`Successfully exported ${contacts.leadName || contacts.leadEmail || 'contact'} to Excel`);
        } else if (Array.isArray(contacts) && contacts.length > 0) {
          // Multiple contacts export
          const timestamp = new Date().toISOString().slice(0, 10);
          const filename = selectedCount > 0
            ? `Selected_Contacts_${selectedCount}_${timestamp}.xlsx`
            : `Contacts_Export_${timestamp}.xlsx`;
          ExportUtils.exportMultipleLeadsToExcel(contacts, filename);
          showNotification(
            selectedCount > 0
              ? `Successfully exported ${selectedCount} contacts to Excel`
              : `Successfully exported ${contacts.length} contacts to Excel`
          );
        }
      } catch (error) {
        showNotification('Failed to export to Excel. Please try again.', 'error');
      }
      onClose();
    } else {
      showNotification('No contacts available to export', 'warning');
    }
  };

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            borderRadius: '20px',
            boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)',
            width: '180px',
            marginTop: '10px',
            marginRight: positionAdjustment.horizontal,
          }
        }}
        MenuListProps={{
          sx: {
            padding: '8px',
          }
        }}
        container={() => document.querySelector('body')}
        keepMounted
      >
        <MenuItem
          onClick={handleExportToCSV}
          sx={{
            py: 1.5,
            borderRadius: '12px',
            '&:hover': {
              backgroundColor: '#f5f5f5',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <FileDownloadOutlinedIcon sx={{ fontSize: 20 }} />
          </ListItemIcon>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              flexGrow: 1,
              paddingRight: '10px',
            }}
          >
            Export to CSV
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={handleExportToExcel}
          sx={{
            py: 1.5,
            borderRadius: '12px',
            '&:hover': {
              backgroundColor: '#f5f5f5',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <InsertDriveFileOutlinedIcon sx={{ fontSize: 20 }} />
          </ListItemIcon>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              flexGrow: 1,
              paddingRight: '10px',
            }}
          >
            Export to Excel
          </Typography>
        </MenuItem>
      </Menu>

      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        handleClose={handleNotificationClose}
        duration={5000}
      />

      <UpgradeSnackBar
        open={isUpgradeSnackbarOpen}
        handleClose={handleUpgradeSnackbarClose}
        handleSubscribe={handleSubscribe}
        message={upgradeMessage}
      />
    </>
  );
};

export default ExportModal;